import { create } from "zustand";

import { HORIZONS, type Horizon } from "../features/matrix-view/model.js";

/** Priorities-view preferences. Device-local by design (like the dev flags):
 *  plain localStorage, never synced or backed up — how wide a decision window
 *  someone wants is a per-device working style, not app data. `dayFocus`
 *  (the load-strip day narrowing) is session-only. */

const LS_KEY = "matrix_view_prefs";

interface MatrixViewPrefs {
  horizon: Horizon;
  includeOverdue: boolean;
  includeUndated: boolean;
  includeDone: boolean;
  includeEvents: boolean;
  /** Daily planning capacity (hours) the load strip colors against; 0 = off. */
  capacityHours: number;
}

interface MatrixViewState extends MatrixViewPrefs {
  /** Local-day key ("2026-07-16") picked on the load strip; null = whole range. */
  dayFocus: string | null;
  setHorizon: (horizon: Horizon) => void;
  toggle: (key: keyof MatrixViewPrefs & `include${string}`) => void;
  setDayFocus: (dayKey: string | null) => void;
  setCapacityHours: (hours: number) => void;
}

const DEFAULTS: MatrixViewPrefs = {
  horizon: "d7",
  includeOverdue: true,
  includeUndated: true,
  includeDone: false,
  includeEvents: true,
  capacityHours: 0,
};

function load(): MatrixViewPrefs {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULTS;
    const v = JSON.parse(raw) as Partial<MatrixViewPrefs>;
    return {
      horizon: HORIZONS.includes(v.horizon as Horizon) ? (v.horizon as Horizon) : DEFAULTS.horizon,
      includeOverdue: typeof v.includeOverdue === "boolean" ? v.includeOverdue : DEFAULTS.includeOverdue,
      includeUndated: typeof v.includeUndated === "boolean" ? v.includeUndated : DEFAULTS.includeUndated,
      includeDone: typeof v.includeDone === "boolean" ? v.includeDone : DEFAULTS.includeDone,
      includeEvents: typeof v.includeEvents === "boolean" ? v.includeEvents : DEFAULTS.includeEvents,
      capacityHours: typeof v.capacityHours === "number" && v.capacityHours >= 0 && v.capacityHours <= 24
        ? v.capacityHours
        : DEFAULTS.capacityHours,
    };
  } catch {
    return DEFAULTS;
  }
}

function persist(state: MatrixViewState) {
  const { horizon, includeOverdue, includeUndated, includeDone, includeEvents, capacityHours } = state;
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ horizon, includeOverdue, includeUndated, includeDone, includeEvents, capacityHours }));
  } catch { /* storage full/blocked — prefs just don't stick */ }
}

export const useMatrixViewStore = create<MatrixViewState>((set, get) => ({
  ...load(),
  dayFocus: null,
  setHorizon(horizon) {
    set({ horizon, dayFocus: null });
    persist(get());
  },
  toggle(key) {
    set({ [key]: !get()[key] } as Partial<MatrixViewState>);
    persist(get());
  },
  setDayFocus(dayKey) {
    set({ dayFocus: dayKey });
  },
  setCapacityHours(hours) {
    set({ capacityHours: Math.max(0, Math.min(24, hours)) });
    persist(get());
  },
}));
